/**
 * Home health demo store — pure reducer.
 *
 * No side effects, no Date.now(): anything time-dependent arrives on the event.
 */
import { linkPlanVisits } from "../engine/bindings";
import { placeHolds, placeReservations } from "../engine/reservationPlacement";
import { deriveConfirmationResets } from "../engine/confirmation";
import { applyReplies, confirmByPhone, delayRound, fireRound, roundKey } from "../engine/confirmationRound";
import type { DemoEvent } from "./events";
import { reconcileOverlay, removeOverlayKeys } from "./localOverlay";
import { EMPTY_LOCAL_OVERLAY, EMPTY_SERVER_SNAPSHOT, type DemoState, type PlanVisit, type WorklistItem } from "@caredna-marlin/utils/homeHealthDemo/state";

export const PRESENTER_LOG_CAP = 200;

/**
 * Every promise on the board, placed in one order.
 *
 * **Chained, not parallel.** Both passes used to be computed from the same state,
 * so neither could see what the other had just done and a hold could be dealt the
 * span a reservation had taken in the same tick. Plan reservations go first
 * because their windows are the narrower ones; the holds then search around them.
 */
function placeAll(state: DemoState): DemoState {
  /**
   * **Holds first.** A hold is a promise to a named patient on a named day; a
   * plan reservation is the branch's own intention and has a window to move
   * inside. Placing reservations first let a recertification with five days of
   * slack take the Monday morning the seed keeps for Okafor's conversion, which
   * pushed his hold off Marcus and onto a per diem, and 1.4 is "Okafor converts
   * on Marcus" (2026-09-24). Reservations then search around the holds, which
   * is the order the two were always described in.
   */
  const withHolds = { ...state, holds: placeHolds(state) };
  return { ...withHolds, planVisits: placeReservations(withHolds) };
}

function mergeItems(existing: WorklistItem[], incoming: WorklistItem[] | undefined): WorklistItem[] {
  if (!incoming || incoming.length === 0) return existing;
  const byId = new Map(existing.map((t) => [t.id, t]));
  for (const t of incoming) byId.set(t.id, t);
  return [...byId.values()];
}

/** Visit ids the demo created or corrected: never a derived "Canceled, rebook" row. */
export function excludedVisitIdsFrom(state: Pick<DemoState, "meta">): number[] {
  return Array.from(new Set([...state.meta.excludedVisitIds, ...(state.meta.createdVisitIds ?? [])])).sort((a, b) => a - b);
}

export function reduce(state: DemoState, event: DemoEvent): DemoState {
  switch (event.type) {
    case "RESET": {
      const excludedVisitIds = Array.from(new Set([...event.seed.meta.excludedVisitIds, ...excludedVisitIdsFrom(state)])).sort((a, b) => a - b);
      /**
       * The reset keeps the server snapshot, so the reservations the fresh seed
       * carries can be placed straight away rather than sitting without an hour
       * until the next poll — a reset has to land whole.
       *
       * **And it keeps the tenant binding for the same reason**, which the fresh
       * seed does not carry. `HorizonFetcher` only dispatches `TENANT_BOUND` when
       * the ids it resolves differ from the last ones it dispatched, and a reset
       * changes neither the snapshot nor the fixtures — so the bindings it would
       * compute are identical, the dispatch is skipped, and dropping them here
       * left the demo unbound until the page was reloaded. No patient resolved,
       * `realDays` was empty so every day fell back to the virtual census, and
       * `convertSoc` threw on `requireBinding`. Pete, 2026-09-22: *"Seems like the
       * reset store doesn't fully reset the demo."*
       *
       * A re-seed that moves the tenant's ids is the one case where these are
       * stale, and it is self-correcting: the new organization changes the
       * snapshot, so the next poll resolves different ids and dispatches them.
       */
      const meta = { ...event.seed.meta, excludedVisitIds, bindings: state.meta.bound ? state.meta.bindings : event.seed.meta.bindings, bound: state.meta.bound || event.seed.meta.bound, realDays: state.meta.bound ? state.meta.realDays : event.seed.meta.realDays };
      // The blocks the demo created are still on the server, so their address lines stay.
      const fresh = { ...event.seed, meta, blockAddresses: { ...state.blockAddresses, ...event.seed.blockAddresses }, server: state.server, local: EMPTY_LOCAL_OVERLAY };
      return placeAll(fresh);
    }

    case "TENANT_RESEEDED":
      return { ...event.seed, server: EMPTY_SERVER_SNAPSHOT, local: EMPTY_LOCAL_OVERLAY };

    case "HYDRATE_EXTERNAL":
      return { ...event.state, server: state.server, local: state.local ?? EMPTY_LOCAL_OVERLAY };

    case "SERVER_SNAPSHOT": {
      // A texted visit that moved (even through the product's own drag-and-drop) is Needs a call,
      // and a visit that has just landed on a day whose round has gone gets its one text.
      const { annotations } = deriveConfirmationResets(state, event.snapshot, event.nowIso);
      const now = event.nowIso ? new Date(event.nowIso) : new Date(event.snapshot.fetchedAtIso ?? Date.now());
      const { local, expired } = reconcileOverlay(state.local ?? EMPTY_LOCAL_OVERLAY, event.snapshot, now);
      const presenter = expired.length
        ? { ...state.presenter, log: capLog([...state.presenter.log, { atIso: now.toISOString(), level: "warn" as const, text: `Optimistic change${expired.length === 1 ? "" : "s"} not confirmed by the server within a minute: ${expired.join(", ")}` }]) }
        : state.presenter;
      const next = { ...state, server: event.snapshot, annotations, local, presenter };
      // Link first, then place: a reservation the horizon has turned into a real
      // visit is no longer a placeholder and must not be given an hour of its own.
      const linked = { ...next, planVisits: linkPlanVisits(next, event.snapshot) };
      return placeAll(linked);
    }

    case "TENANT_BOUND": {
      const next = { ...state, meta: { ...state.meta, bindings: event.bindings, realDays: event.realDays, bound: true } };
      const linked = { ...next, planVisits: linkPlanVisits(next, state.server) };
      return placeAll(linked);
    }

    case "ROUND_DELAYED": {
      const confirmationRounds = delayRound(state.confirmationRounds, event.providerId, event.forDate);
      return confirmationRounds === state.confirmationRounds ? state : { ...state, confirmationRounds };
    }

    case "ROUND_FIRED": {
      const key = roundKey(event.providerId, event.forDate);
      if (state.confirmationRounds[key]?.firedAtIso) return state;
      const { annotations } = fireRound(state, event.providerId, event.forDate, event.nowIso);
      return {
        ...state,
        annotations,
        confirmationRounds: { ...state.confirmationRounds, [key]: { ...(state.confirmationRounds[key] ?? { delayHours: 0 }), firedAtIso: event.nowIso } },
      };
    }

    case "ROUND_REPLIES": {
      const key = roundKey(event.providerId, event.forDate);
      if (state.confirmationRounds[key]?.cutoffAtIso) return state;
      const annotations = applyReplies(state, event.providerId, event.forDate, event.replies, event.nowIso);
      return {
        ...state,
        annotations,
        confirmationRounds: { ...state.confirmationRounds, [key]: { ...(state.confirmationRounds[key] ?? { delayHours: 0 }), cutoffAtIso: event.nowIso } },
      };
    }

    case "VISIT_CONFIRMED_BY_PHONE": {
      const annotations = confirmByPhone(state, event.realVisitId, event.nowIso);
      return annotations === state.annotations ? state : { ...state, annotations };
    }

    case "ANNOTATION_UPSERT":
      return { ...state, annotations: { ...state.annotations, [String(event.annotation.realVisitId)]: event.annotation } };

    case "ROWS_UPSERT": {
      if (event.items.length === 0) return state;
      return { ...state, worklistItems: mergeItems(state.worklistItems, event.items) };
    }

    case "ROW_RESOLVED":
      return { ...state, worklistItems: state.worklistItems.map((t) => (t.id === event.id ? { ...t, resolved: true } : t)) };

    case "CALLOUT_OPENED":
      return { ...state, presenter: { ...state.presenter, callOut: { providerHandle: event.providerHandle, date: event.date, startedAtIso: event.startedAtIso, shiftId: event.shiftId } } };

    case "CALLOUT_CLOSED":
      return { ...state, presenter: { ...state.presenter, callOut: undefined } };

    case "REFERRAL_ARRIVED": {
      const referral = state.referrals[event.referralId];
      if (!referral || referral.status !== "pending") return state;
      return { ...state, referrals: { ...state.referrals, [event.referralId]: { ...referral, status: "new", receivedAtIso: event.nowIso } } };
    }

    /**
     * **A referral keyed in by hand** (2026-09-24), for a prospect with no
     * referral system to show. The patient has already been created in the
     * tenant, so it is bound here by the id the mutation returned rather than
     * waiting for the horizon poll to match it by name — the referral panel asks
     * the product's scheduler about that patient the moment it opens, and an
     * unbound patient has nothing to ask about.
     *
     * It arrives already `new`: somebody typing it in *is* the arrival, so there
     * is no presenter event to fire afterwards.
     */
    case "REFERRAL_KEYED": {
      const meta = { ...state.meta, bindings: { ...state.meta.bindings, patientIdsByRef: { ...state.meta.bindings.patientIdsByRef, [event.patient.ref]: event.serverPatientId } } };
      return placeAll({
        ...state,
        meta,
        patients: { ...state.patients, [event.patient.ref]: { ...event.patient, serverId: event.serverPatientId } },
        referrals: { ...state.referrals, [event.referral.id]: event.referral },
      });
    }

    /**
     * The branch says no to a referral it never accepted. Nothing was promised,
     * so there is nothing to give back: it leaves the queue and the panel says
     * what happened to it.
     */
    case "REFERRAL_DECLINED": {
      const referral = state.referrals[event.referralId];
      if (!referral || referral.status !== "new") return state;
      return { ...state, referrals: { ...state.referrals, [event.referralId]: { ...referral, status: "declined" } } };
    }

    case "REFERRAL_ACCEPTED": {
      const referral = state.referrals[event.referralId];
      if (!referral) return state;
      // The hold is the tighter window, so anything looser it has landed on — a
      // recertification with five days to happen in — re-places itself now.
      const accepted: DemoState = {
        ...state,
        referrals: { ...state.referrals, [event.referralId]: { ...referral, status: "accepted" as const } },
        holds: { ...state.holds, [event.hold.id]: { ...event.hold, status: event.hold.status ?? "held" } },
        readiness: { ...state.readiness, [event.hold.id]: event.readiness },
      };
      return placeAll(accepted);
    }

    case "REFERRAL_DISCHARGE_CHANGED": {
      const referral = state.referrals[event.referralId];
      if (!referral || referral.expectedDischarge === event.expectedDischarge) return state;
      return { ...state, referrals: { ...state.referrals, [event.referralId]: { ...referral, expectedDischarge: event.expectedDischarge, dischargeChangedAtIso: event.nowIso } } };
    }

    case "REFERRAL_PLAN_SET": {
      const referral = state.referrals[event.referralId];
      if (!referral) return state;
      // Clearing the choice puts the referral back on the suggestion.
      const next = { ...referral, expectedPlan: event.plan };
      if (!event.plan) delete next.expectedPlan;
      return { ...state, referrals: { ...state.referrals, [event.referralId]: next } };
    }

    case "HOLD_MOVED": {
      const hold = state.holds[event.holdId];
      if (!hold || hold.status !== "held") return state;
      const referral = state.referrals[hold.referralId];
      const referrals = referral?.dischargeChangedAtIso ? { ...state.referrals, [referral.id]: { ...referral, dischargeChangedAtIso: undefined } } : state.referrals;
      const moved = {
        ...state,
        referrals,
        holds: { ...state.holds, [event.holdId]: { ...hold, providerHandle: event.slot.providerHandle, date: event.slot.date, windowStart: event.slot.windowStart, windowEnd: event.slot.windowEnd } },
      };
      return placeAll(moved);
    }

    case "HOLD_RELEASED": {
      const hold = state.holds[event.holdId];
      if (!hold || hold.status !== "held") return state;
      const referral = state.referrals[hold.referralId];
      return {
        ...state,
        holds: { ...state.holds, [event.holdId]: { ...hold, status: "released" } },
        referrals: referral ? { ...state.referrals, [referral.id]: { ...referral, status: "released" } } : state.referrals,
      };
    }

    case "WELCOME_CALL_COMPLETED": {
      const patient = state.patients[event.patientRef];
      if (!patient) return state;
      const readiness = { ...state.readiness };
      for (const h of Object.values(state.holds)) {
        const r = readiness[h.id];
        if (h.patientRef === event.patientRef && r && r.readinessCall !== undefined) readiness[h.id] = { ...r, readinessCall: "done" };
      }
      return {
        ...state,
        patients: { ...state.patients, [event.patientRef]: { ...patient, constraints: event.constraints } },
        readiness,
      };
    }

    case "READINESS_UPDATED": {
      const r = state.readiness[event.holdId];
      if (!r || r[event.gate] === undefined || !state.holds[event.holdId]) return state;
      return { ...state, readiness: { ...state.readiness, [event.holdId]: { ...r, [event.gate]: event.status } } };
    }

    case "HOLD_CONVERTED": {
      const hold = state.holds[event.holdId];
      if (!hold) return state;
      return {
        ...state,
        holds: { ...state.holds, [event.holdId]: { ...hold, status: "converted", convertedVisitId: event.realVisitId } },
        episodes: { ...state.episodes, [event.episode.id]: event.episode },
        annotations: { ...state.annotations, [String(event.realVisitId)]: event.annotation },
      };
    }

    case "PLAN_TRACK_ADDED": {
      const episode = state.episodes[event.episodeId];
      if (!episode) return state;
      const added: Record<string, PlanVisit> = {};
      for (const v of event.planVisits) added[v.id] = v;
      return {
        ...state,
        episodes: {
          ...state.episodes,
          [event.episodeId]: { ...episode, versions: [...episode.versions, event.version], pendingDisciplines: episode.pendingDisciplines.filter((p) => p.discipline !== event.removePending) },
        },
        planVisits: { ...state.planVisits, ...added },
      };
    }

    case "PLAN_VISIT_UPDATED":
      return { ...state, planVisits: { ...state.planVisits, [event.planVisit.id]: event.planVisit } };

    case "PATIENT_CONSTRAINTS_SET": {
      const patient = state.patients[event.patientRef];
      if (!patient) return state;
      return { ...state, patients: { ...state.patients, [event.patientRef]: { ...patient, constraints: event.constraints } } };
    }

    // A drop is a proposal and a write is a commit. My day stages its drops in
    // the panel's own memory and only **Save** dispatches this, so nothing moves
    // under the clinician's hand; My week's cross-day drop *is* the commit and
    // dispatches it at once. Either way, a reservation the committed batch has
    // double-booked re-places itself inside its window here. My day's **Reset**
    // button discards the staged drops and dispatches nothing at all, so it
    // leaves every reservation exactly where it is. (Presenter → **Reset this browser**
    // is a different thing: it rebuilds the seed, and `RESET` above places that
    // fresh seed's reservations.)
    case "LOCAL_VISIT_MOVED": {
      const next = { ...state, local: { ...state.local, visits: { ...state.local.visits, [String(event.change.visitId)]: event.change } } };
      return placeAll(next);
    }

    case "LOCAL_VISIT_CANCELED": {
      const vr = state.server.visitResources.find((v) => v.visitId === event.visitId);
      const prior = state.local.visits[String(event.visitId)];
      const change = { visitId: event.visitId, providerId: prior?.providerId ?? vr?.providerId, startIso: prior?.startIso ?? vr?.startIso ?? event.appliedAtIso, endIso: prior?.endIso ?? vr?.endIso ?? event.appliedAtIso, canceled: true, appliedAtIso: event.appliedAtIso };
      return { ...state, local: { ...state.local, visits: { ...state.local.visits, [String(event.visitId)]: change } } };
    }

    case "LOCAL_VISIT_ADDED":
      return { ...state, local: { ...state.local, addedVisits: [...state.local.addedVisits.filter((v) => v.tempId !== event.visit.tempId), event.visit] } };

    case "LOCAL_BLOCK_CANCELED":
      return { ...state, local: { ...state.local, canceledBlocks: { ...state.local.canceledBlocks, [String(event.blockId)]: { blockId: event.blockId, appliedAtIso: event.appliedAtIso } } } };

    case "LOCAL_BLOCK_MOVED": {
      const next = { ...state, local: { ...state.local, movedBlocks: { ...state.local.movedBlocks, [String(event.change.blockId)]: event.change } } };
      return placeAll(next);
    }

    case "LOCAL_BLOCK_ADDED":
      return { ...state, local: { ...state.local, blocks: [...state.local.blocks.filter((b) => b.tempId !== event.block.tempId), event.block] } };

    case "LOCAL_ROLLBACK": {
      const local = removeOverlayKeys(state.local, event.keys);
      return local === state.local ? state : { ...state, local };
    }

    case "META_PATCH":
      return { ...state, meta: { ...state.meta, ...event.patch, schemaVersion: state.meta.schemaVersion } };

    case "CONFIG_PATCH":
      return { ...state, config: { ...state.config, ...event.patch } };

    case "SANDBOX_SET":
      return { ...state, sandbox: event.sandbox };

    case "VISIT_CREATED_BY_DEMO":
      return state.meta.createdVisitIds.includes(event.visitId) ? state : { ...state, meta: { ...state.meta, createdVisitIds: [...state.meta.createdVisitIds, event.visitId] } };

    case "BLOCK_ADDRESS_RECORDED":
      return { ...state, blockAddresses: { ...state.blockAddresses, [`id:${event.blockId}`]: event.address } };

    case "PRESENTER_LOG":
      return { ...state, presenter: { ...state.presenter, log: capLog([...state.presenter.log, event.line]) } };

    case "PRESENTER_FIRED":
      return state.presenter.fired.includes(event.key)
        ? state
        : { ...state, presenter: { ...state.presenter, fired: [...state.presenter.fired, event.key] } };

    default: {
      const exhaustive: never = event;
      return exhaustive;
    }
  }
}

function capLog(log: DemoState["presenter"]["log"]): DemoState["presenter"]["log"] {
  return log.length > PRESENTER_LOG_CAP ? log.slice(-PRESENTER_LOG_CAP) : log;
}
